import React, { useEffect, useRef } from 'react';
import { Box, Typography } from '@mui/material';
import { Chart as ChartJS, registerables } from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';

ChartJS.register(...registerables, ChartDataLabels);

// Default color palette for project types
const defaultColors = [
    '#475890', '#5c6daa', '#7483c2', '#a5aee1', '#bec4ec',
    '#6b8e9f', '#8fa7b5', '#b3c4ce', '#d1dde3', '#e8eff2'
];

const TypesOfProjectsChart = ({ data = [] }) => {
    const donutRef = useRef(null);
    const donutInstance = useRef(null);

    // Default data if none provided
    const defaultData = [
        { type: 'Course Projects', count: 30 },
        { type: 'Competitions', count: 25 },
        { type: 'Professor Projects', count: 20 },
        { type: 'Self Projects', count: 15 },
        { type: 'Internships', count: 10 },
    ];

    const chartData = data.length > 0 ? data : defaultData;

    // Map data to segments with colors
    const segments = chartData.map((item, index) => ({
        label: item.type || item.label || `Type ${index + 1}`,
        value: item.count || item.value || 0,
        color: defaultColors[index % defaultColors.length],
    }));

    // Calculate total for percentage
    const total = segments.reduce((sum, s) => sum + s.value, 0);

    useEffect(() => {
        if (!donutRef.current) return;

        if (donutInstance.current) {
            donutInstance.current.destroy();
        }

        const ctx = donutRef.current.getContext('2d');
        if (!ctx) return;

        donutInstance.current = new ChartJS(ctx, {
            type: 'doughnut',
            data: {
                labels: segments.map(s => s.label),
                datasets: [
                    {
                        data: segments.map(s => s.value),
                        backgroundColor: segments.map(s => s.color),
                        borderColor: 'transparent',
                        borderWidth: 0,
                        borderRadius: 999,
                        spacing: 8,
                        hoverOffset: 0,
                    },
                ],
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                cutout: '62%',
                rotation: -0.5 * Math.PI,
                layout: {
                    padding: 60,
                },
                plugins: {
                    legend: { display: false },
                    tooltip: {
                        enabled: true,
                        callbacks: {
                            label: (context) => {
                                const value = context.raw;
                                const percentage = total > 0 ? ((value / total) * 100).toFixed(1) : 0;
                                return `${context.label}: ${value} (${percentage}%)`;
                            }
                        }
                    },
                    datalabels: {
                        color: context => segments[context.dataIndex]?.color || '#333',
                        font: {
                            size: 14,
                            weight: 500,
                            family: 'Roboto-Medium, Roboto, sans-serif',
                        },
                        anchor: 'end',
                        align: 'end',
                        offset: 10,
                        formatter: (value, context) => {
                            const percentage = total > 0 ? ((value / total) * 100).toFixed(0) : 0;
                            return (
                                percentage + '%\n' + context.chart.data.labels[context.dataIndex]
                            );
                        },
                        textAlign: 'center',
                    },
                },
            },
        });

        return () => {
            if (donutInstance.current) {
                donutInstance.current.destroy();
                donutInstance.current = null;
            }
        };
    }, [data, segments, total]);

    // If no data, show message
    if (data.length === 0 && chartData === defaultData) {
        // Use default data for display
    }

    return (
        <Box sx={{ width: '100%', height: '100%', position: 'relative' }}>
            {total === 0 ? (
                <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '100%'
                }}>
                    <Typography sx={{ color: '#999', fontSize: '14px' }}>
                        No project data available
                    </Typography>
                </Box>
            ) : (
                <canvas ref={donutRef} style={{ width: '100%', height: '100%' }} />
            )}
        </Box>
    );
};

export default TypesOfProjectsChart;
